import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import { AddWidgetClickBiEvent, BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PlusIcon from '@wix/da-ds/pkg/Icons/Plus';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import WidgetTypeCard from './WidgetTypeCard';
import { InstallableWidget } from '../../types/modules';
import usePrivileges from '../../hooks/usePrivileges';

import s from './AddWidget.scss';

export interface Props {
  position?: number;
  installableWidgets: InstallableWidget[];
  customLabel?: string;
  disabled?: boolean; // used when installing a module, use is installing from the hook and render a loading indicator here
}

const AddWidget: React.FC<Props> = ({ position, installableWidgets, customLabel, disabled }) => {
  const { t } = useTranslation();
  const { canAddModule } = usePrivileges();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useOnOutsideClick([rootRef], () => setIsMenuOpen(false), true);

  if (!canAddModule) {
    return null;
  }

  const openMenu = () => setIsMenuOpen(true);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <ErrorBoundary debugComponent="AddWidget" hideOnFail>
      <div
        className={classnames(
          s['root'],
          disabled && s['disabled'],
          isMenuOpen && s['menu-is-open']
        )}
        data-role="add-widget"
        ref={rootRef}
      >
        <div className={s['line']}>
          <PlainButtonOrLink
            biData={
              isMenuOpen
                ? BiData<DefaultBiEvent>({
                    evid: 2,
                    click_info: 'addWidget.close',
                  })
                : BiData<AddWidgetClickBiEvent>({
                    evid: 282,
                  })
            }
            onClick={() => {
              isMenuOpen ? closeMenu() : openMenu();
            }}
            className={s['button-with-gradient-line']}
          >
            <div className={s['menu-toggle-btn']}>
              <PlusIcon className={s['plus-icon']} size={IconSize.SMALL} />
              {!isMenuOpen ? customLabel || t('gruser.layout.button.addWidget') : t('common.close')}
            </div>
          </PlainButtonOrLink>

          {isMenuOpen && (
            <div className={s['menu-container']}>
              <div className={s['menu']} data-role="menu">
                {installableWidgets.map((installableWidget, index: number) => {
                  return (
                    <div
                      className={s['menu-item']}
                      key={index}
                      onClick={() => {
                        setIsMenuOpen(false);
                      }}
                    >
                      <WidgetTypeCard
                        position={position || installableWidget.module.position || 0}
                        installableWidget={installableWidget}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

AddWidget.displayName = 'AddWidget';

export default AddWidget;
